/**
 * Party Colors Constants
 *
 * Discrete color definitions for election map intensity levels.
 * Each intensity level (50�&ndash;80�) has separate light and dark mode hex values.
 *
 * Colors are defined as CSS custom properties in module.pcss
 * (--color-democrat-map-80, --color-republican-map-70, etc.)
 * This file provides the JS-side lookup for SVG fill attributes.
 */

/** Intensity levels from API mapped to cent labels */
const INTENSITY_TO_CENTS: Record<number, number> = {
  0: 50, // toss-up
  1: 60, // lean
  2: 70, // likely
  3: 80, // solid
};

type CentsKey = 50 | 60 | 70 | 80;

interface PartyColorMap {
  readonly democrat: Record<CentsKey, string>;
  readonly republican: Record<CentsKey, string>;
  readonly nonpartisan: Record<CentsKey, string>;
  readonly neutral: string;
  readonly tie: string;
}

/** Light mode map colors */
const LIGHT_COLORS: PartyColorMap = {
  democrat: {
    80: '#0055D4',
    70: '#307EF2',
    60: '#5998F5',
    50: '#83B2F7',
  },
  republican: {
    80: '#D80E1F',
    70: '#FC3243',
    60: '#FD5B69',
    50: '#FD848E',
  },
  nonpartisan: {
    80: '#7C3AED',
    70: '#8B5CF6',
    60: '#A78BFA',
    50: '#C4B5FD',
  },
  neutral: '#D0D1D2',
  tie: '#EBD568AA',
};

/** Dark mode map colors */
const DARK_COLORS: PartyColorMap = {
  democrat: {
    80: '#0055D4',
    70: '#0449B0',
    60: '#093D8C',
    50: '#0D3167',
  },
  republican: {
    80: '#D80E1F',
    70: '#B1101F',
    60: '#8A121F',
    50: '#64151F',
  },
  nonpartisan: {
    80: '#7C3AED',
    70: '#6D28D9',
    60: '#5B21B6',
    50: '#4C1D95',
  },
  neutral: '#383B41',
  tie: '#EBD568AA',
};

/**
 * Get party color for map fill at a given intensity level.
 *
 * @param party - Party name from API ('Democrat', 'Republican', 'Independent')
 * @param intensity - Color intensity from API (0-3)
 * @param isDark - Whether dark mode is active
 * @returns Hex color string
 */
export function getPartyColor(party: string, intensity: number, isDark: boolean): string {
  const colorMap = isDark ? DARK_COLORS : LIGHT_COLORS;

  if (!party) return colorMap.tie;

  const cents = (INTENSITY_TO_CENTS[intensity] ?? 80) as CentsKey;
  const partyKey = party === 'Republican' ? 'republican' : party === 'Nonpartisan' ? 'nonpartisan' : 'democrat';

  return colorMap[partyKey][cents];
}

/**
 * Get the neutral (no-data) color for map states.
 *
 * @param isDark - Whether dark mode is active
 * @returns Hex color string
 */
export function getMapNeutralColor(isDark: boolean): string {
  return isDark ? DARK_COLORS.neutral : LIGHT_COLORS.neutral;
}
